import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Body overflow/padding'e hiç dokunmayan, portal tabanlı özel modal.
 * React Bootstrap Modal'ın scrollbar telafisinden kaynaklanan layout kaymasını önler.
 *
 * Props:
 *   show            – boolean
 *   onHide          – () => void
 *   title           – string | ReactNode
 *   icon            – ReactNode (isteğe bağlı)
 *   headerGradient  – CSS gradient string (varsayılan: mavi)
 *   size            – 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen'
 *   children        – modal gövdesi
 *   footer          – ReactNode (isteğe bağlı)
 *   footerStyle     – ek style objesi (isteğe bağlı)
 */
export const KamaModal = ({
    show,
    onHide,
    title,
    icon,
    headerGradient = 'linear-gradient(135deg, #1e2a38 0%, #2563a8 100%)',
    size = 'lg',
    children,
    footer,
    footerStyle = {},
}) => {
    // Escape tuşuyla kapat
    useEffect(() => {
        if (!show) return;
        const handler = (e) => { if (e.key === 'Escape') onHide(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [show, onHide]);

    if (!show) return null;

    const isFullscreen = size === 'fullscreen';
    const widthMap = { sm: 480, md: 620, lg: 820, xl: 1120 };
    const maxWidth = widthMap[size] || 820;

    const dialogStyle = isFullscreen
        ? { position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', flexDirection: 'column' }
        : {
            position: 'fixed', zIndex: 1050,
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: `min(${maxWidth}px, 96vw)`,
            maxHeight: '90vh',
            display: 'flex', flexDirection: 'column',
            borderRadius: '12px', overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
        };

    return createPortal(
        <>
            {/* Backdrop – tıklanınca kapat */}
            <div
                onClick={onHide}
                style={{
                    position: 'fixed', inset: 0, zIndex: 1049,
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(2px)',
                }}
            />

            {/* Dialog */}
            <div style={dialogStyle}>

                {/* Header */}
                <div style={{
                    background: headerGradient,
                    padding: '18px 24px', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div style={{
                        color: '#fff', fontWeight: 700, fontSize: '16px',
                        display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                        {icon} {title}
                    </div>
                    <button
                        onClick={onHide}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
                        style={{
                            background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff',
                            width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '13px', transition: 'background 0.15s', flexShrink: 0,
                        }}
                    >✕</button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: '#fff' }}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div style={{
                        flexShrink: 0, padding: '14px 24px',
                        background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'flex-end',
                        ...footerStyle,
                    }}>
                        {footer}
                    </div>
                )}
            </div>
        </>,
        document.body
    );
};
